import { useRunStore } from '../store/runStore';
import { useT, type TKey } from '../i18n';
import { useUserSettings } from '../userSettings';
import { xpToNextLevel } from '../game/systems/xp';

// Cradle palette hex values — mirrored from .run-hud tokens (no CSS var leak needed).
const HUD_GOLD_HI   = '#ffd866';
const HUD_GOLD      = '#f5c542';
const HUD_CYAN      = '#3dd5ff';
const HUD_VIOLET_HI    = '#d8b3ff';
const HUD_VIOLET_FAINT = 'rgba(183,107,255,0.6)';
const HUD_GREEN_HI  = '#6cf591';
const HUD_WARN      = '#ffae3b';
const HUD_DANGER    = '#ff4a4a';
const HUD_TX_DIM    = 'rgba(255,255,255,0.58)';
const HUD_TX_FAINT  = 'rgba(255,255,255,0.35)';
const HUD_BG_CARD   = 'rgba(13, 20, 33, 0.82)';

export function HUD() {
  const hp        = useRunStore((s) => s.catStats.hp);
  const maxHp     = useRunStore((s) => s.catStats.maxHp);
  const roomIdx   = useRunStore((s) => s.currentRoomIndex);
  const layout    = useRunStore((s) => s.layout);
  const kills     = useRunStore((s) => s.enemiesKilled);
  const level             = useRunStore((s) => s.level);
  const xp                = useRunStore((s) => s.xp);
  const sectorTimeLeftSec = useRunStore((s) => s.sectorTimeLeftSec);
  const ownedPerks = useRunStore((s) => s.ownedPerks);
  const appliedEvolutions = useRunStore((s) => s.appliedEvolutions);
  const secured   = useRunStore((s) => s.lootCount);
  const atRisk    = useRunStore((s) => s.lootAtRisk);
  // Phase 3h: storm sector-mod overlay. When stormActive flips true the HUD
  // gets an intensified scanline veil to telegraph "shields are offline".
  const stormActive = useRunStore((s) => s.stormActive);
  const { t }             = useT();
  const userSettings      = useUserSettings();
  const animationsEnabled = !userSettings.reducedMotion;

  if (!layout) return null;

  const totalRooms = layout.rooms.length;

  const pct        = Math.max(0, Math.min(100, (hp / maxHp) * 100));
  const isLow      = pct > 0 && pct <= 30;
  const isCritical = pct > 0 && pct <= 15;

  const xpNeed = xpToNextLevel(level);
  const xpPct = Math.max(0, Math.min(100, (xp / xpNeed) * 100));
  const mmss = `${Math.floor(sectorTimeLeftSec / 60)}:${String(sectorTimeLeftSec % 60).padStart(2, '0')}`;

  // HP bar fill class
  const hpFillClass = isCritical ? 'hud-bar-fill crit' : isLow ? 'hud-bar-fill warn' : 'hud-bar-fill';
  // HP label colour
  const hpLabelColor = isCritical ? HUD_DANGER : isLow ? HUD_WARN : HUD_GREEN_HI;
  const hpLabelShadow = isCritical
    ? `0 0 10px ${HUD_DANGER}`
    : isLow
      ? `0 0 8px ${HUD_WARN}`
      : `0 0 6px rgba(108,245,145,0.55)`;

  const outerStyle: React.CSSProperties = {
    position: 'fixed',
    top: 'calc(env(safe-area-inset-top, 0px) + var(--space-2))',
    left: 'var(--space-2)',
    right: 'var(--space-2)',
    pointerEvents: 'none',
    zIndex: 5,
    fontFamily: 'inherit',
    color: HUD_TX_DIM,
    textShadow: '0 1px 3px rgba(0,0,0,0.8)',
  };

  return (
    <>
      {isLow && animationsEnabled && <div className="low-hp-vignette" aria-hidden="true" />}
      {/* Phase 3h: storm sector-mod veil. .scanlines class already exists from
          Phase 2j; we just lay it over the HUD with intensified opacity while
          stormActive. pointerEvents:none so it never blocks taps. */}
      {stormActive && (
        <div
          className="scanlines"
          aria-hidden="true"
          style={{
            position: 'fixed', inset: 0, pointerEvents: 'none', zIndex: 1,
            opacity: 0.65, mixBlendMode: 'screen',
          }}
        />
      )}
      <div className="run-hud" style={outerStyle}>
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: 'var(--space-2)',
        }}>

          {/* ── HP block ──────────────────────────────────── */}
          <div
            className={isCritical && animationsEnabled ? 'heartbeat' : ''}
            style={{
              minWidth: '9rem',
              flex: '0 0 auto',
              background: HUD_BG_CARD,
              borderRadius: 10,
              padding: '6px 10px 6px',
              border: `1px solid rgba(61,213,255,0.14)`,
              boxShadow: '0 2px 12px rgba(0,0,0,0.45)',
            }}
          >
            <div
              className="label"
              style={{ color: hpLabelColor, marginBottom: 4, textShadow: hpLabelShadow, fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}
            >
              {isCritical ? `⚠ ${t('hud.vitalsCritical')}` : `◉ ${t('hud.vitals')}`}
            </div>

            {/* HP bar using global .hud-bar-* classes */}
            <div className="hud-bar-shell">
              <div className={hpFillClass} style={{ width: `${pct}%` } as React.CSSProperties} />
              {[25, 50, 75].map((seg) => (
                <span
                  key={seg}
                  className="hud-bar-tick"
                  style={{ left: `${seg}%` } as React.CSSProperties}
                />
              ))}
            </div>

            <div style={{ fontSize: '0.62rem', marginTop: 3, fontVariantNumeric: 'tabular-nums', color: HUD_TX_DIM }}>
              HP: {hp} / {maxHp}
            </div>
          </div>

          {/* ── Sector progress + kills ───────────────────── */}
          <div style={{
            textAlign: 'right',
            minWidth: '8rem',
            background: HUD_BG_CARD,
            borderRadius: 10,
            padding: '6px 10px 6px',
            border: `1px solid rgba(61,213,255,0.14)`,
            boxShadow: '0 2px 12px rgba(0,0,0,0.45)',
          }}>
            <div className="label" style={{ color: HUD_CYAN, marginBottom: 3, fontSize: '0.62rem', letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              ◆ {t('hud.sector')}
            </div>

            {/* Room dots */}
            <div style={{
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '4px',
              marginBottom: 4,
              fontSize: '0.85rem',
              letterSpacing: '0.1em',
            }}>
              {layout.rooms.map((r, i) => {
                const isCurrent   = i === roomIdx;
                const isPast      = i < roomIdx;
                const filled      = isCurrent || isPast;
                const isExtraction = r.type === 'extraction';
                const dotColor = isExtraction
                  ? HUD_GREEN_HI
                  : isPast
                    ? 'rgba(61,213,255,0.45)'
                    : isCurrent
                      ? HUD_CYAN
                      : HUD_TX_FAINT;
                return (
                  <span
                    key={i}
                    className={isCurrent ? 'pulse' : ''}
                    style={{
                      color: dotColor,
                      fontSize: isCurrent ? '1.1rem' : '0.85rem',
                      lineHeight: 1,
                    }}
                  >
                    {filled ? '●' : isExtraction ? '◆' : '○'}
                  </span>
                );
              })}
            </div>

            <div style={{ fontSize: '0.62rem', fontVariantNumeric: 'tabular-nums', color: HUD_TX_DIM }}>
              {t('hud.roomLabel')} {roomIdx + 1} / {totalRooms}
            </div>
            <div style={{ fontSize: '0.62rem', opacity: 0.7, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
              {t('hud.kills')} {kills}
            </div>
            <div style={{ fontSize: '0.62rem', color: HUD_GREEN_HI, fontVariantNumeric: 'tabular-nums', marginTop: 2 }}>
              LV {level} · ⏱ <span>{mmss}</span>
            </div>
            <div className="hud-bar-shell" style={{ marginTop: 3, height: 4 }}>
              <div className="hud-bar-fill" style={{ width: `${xpPct}%`, background: HUD_GREEN_HI } as React.CSSProperties} />
            </div>

            {/* Loot counters */}
            <div style={{
              marginTop: 4,
              display: 'inline-flex',
              alignItems: 'center',
              gap: 'var(--space-1)',
              fontVariantNumeric: 'tabular-nums',
            }}>
              <span
                style={{ fontSize: '0.7rem', color: HUD_GOLD }}
                aria-label={t('loot.secured' as TKey)}
              >
                {secured} 🐟
              </span>
              {atRisk > 0 && (
                <span
                  style={{
                    fontSize: '0.7rem',
                    color: HUD_DANGER,
                    animation: animationsEnabled ? 'pulse-glow 1.2s ease-in-out infinite' : undefined,
                  }}
                  aria-label={t('loot.atRisk' as TKey)}
                >
                  +{atRisk}⚠
                </span>
              )}
            </div>
          </div>
        </div>

        {/* ── Perks / tech badges ───────────────────────── */}
        {ownedPerks.length > 0 && (
          <div style={{
            marginTop: 'var(--space-2)',
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
          }}>
            <span className="label" style={{
              color: HUD_TX_FAINT,
              alignSelf: 'center',
              marginRight: 4,
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
              {t('hud.tech')}
            </span>
            {ownedPerks.map((id, i) => {
              const localizedName = t(`perk.${id}.name` as TKey);
              const letter = localizedName.charAt(0);
              return (
                <span
                  key={`${id}-${i}`}
                  title={localizedName}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    border: `1px solid rgba(245,197,66,0.45)`,
                    borderRadius: 5,
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    color: HUD_GOLD_HI,
                    background: 'rgba(245,197,66,0.10)',
                    boxShadow: '0 0 6px rgba(245,197,66,0.25)',
                  } as React.CSSProperties}
                >
                  {letter}
                </span>
              );
            })}
          </div>
        )}

        {/* ── Evolution badges (Phase 2u) ────────────────── */}
        {appliedEvolutions.size > 0 && (
          <div style={{
            marginTop: 4,
            display: 'flex',
            flexWrap: 'wrap',
            gap: '4px',
          }}>
            <span className="label" style={{
              color: HUD_VIOLET_FAINT,
              alignSelf: 'center',
              marginRight: 4,
              fontSize: '0.6rem',
              letterSpacing: '0.08em',
              textTransform: 'uppercase',
            }}>
              {t('hud.evo')}
            </span>
            {[...appliedEvolutions].map((id) => {
              const shortName = t(`evolution.${id}.shortName` as TKey);
              const letter = shortName.charAt(0);
              return (
                <span
                  key={id}
                  title={shortName}
                  style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: 22,
                    height: 22,
                    border: `1px solid rgba(183,107,255,0.45)`,
                    borderRadius: 5,
                    fontSize: '0.7rem',
                    fontWeight: 'bold',
                    color: HUD_VIOLET_HI,
                    background: 'rgba(183,107,255,0.10)',
                    boxShadow: '0 0 6px rgba(183,107,255,0.25)',
                  } as React.CSSProperties}
                >
                  {letter}
                </span>
              );
            })}
          </div>
        )}
      </div>
    </>
  );
}
